import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { UserReplica, ReplicaFormData, ReplicaStatus, ReplicaContextType, AIAgent, TeamHierarchyNode, HierarchyUpdate } from '../types';
import { agents } from '../data/agentsData';
import { logger } from '../lib/logger';
import { db } from '../lib/firebase';
import { useAuth } from './AuthContext';
import {
  collection,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  query,
  where,
  serverTimestamp,
} from 'firebase/firestore';

const ReplicaContext = createContext<ReplicaContextType | undefined>(undefined);

export const useReplica = () => {
  const context = useContext(ReplicaContext);
  if (context === undefined) {
    throw new Error('useReplica must be used within a ReplicaProvider');
  }
  return context;
};

interface ReplicaProviderProps {
  children: ReactNode;
}

const STORAGE_KEY = 'humanreplica_user_replicas';
const COLLECTION_NAME = 'replicas';

// Sanitize replica data for Firestore (remove File objects and undefined values)
const sanitizeForFirestore = (obj: any): any => {
  if (obj === null || obj === undefined) return null;
  if (obj instanceof File || obj instanceof Blob) return null;
  if (Array.isArray(obj)) return obj.map(sanitizeForFirestore);
  if (typeof obj === 'object' && obj !== null) {
    const sanitized: Record<string, any> = {};
    for (const [key, value] of Object.entries(obj)) {
      const sanitizedValue = sanitizeForFirestore(value);
      if (sanitizedValue !== undefined) {
        sanitized[key] = sanitizedValue;
      }
    }
    return sanitized;
  }
  return obj;
};

// Load from localStorage as initial cache
const loadCachedReplicas = (): UserReplica[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      logger.debug('Loaded replicas from cache', { count: parsed.length });
      return parsed;
    }
  } catch (error) {
    logger.error('Failed to load replicas from cache:', error);
  }
  return [];
};

export const ReplicaProvider: React.FC<ReplicaProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [userReplicas, setUserReplicas] = useState<UserReplica[]>(loadCachedReplicas);
  const [firestoreReady, setFirestoreReady] = useState(false);

  // Subscribe to Firestore realtime updates when user is authenticated
  useEffect(() => {
    if (!user?.uid) {
      setUserReplicas([]);
      setFirestoreReady(false);
      return;
    }

    logger.debug('Subscribing to Firestore replicas', { userId: user.uid });

    const q = query(
      collection(db, COLLECTION_NAME),
      where('creatorId', '==', user.uid)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const replicas: UserReplica[] = [];
        snapshot.forEach((docSnap) => {
          const data = docSnap.data();
          replicas.push({
            ...data,
            id: docSnap.id,
          } as UserReplica);
        });

        logger.debug('Firestore replicas synced', { count: replicas.length });
        setUserReplicas(replicas);
        setFirestoreReady(true);

        // Update localStorage cache
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(replicas));
        } catch (e) {
          // localStorage full or unavailable — not critical
        }
      },
      (error) => {
        logger.error('Firestore subscription error:', error);
        // Fall back to cached data (already in state)
        setFirestoreReady(true);
      }
    );

    return () => unsubscribe();
  }, [user?.uid]);

  const generateReplicaId = (): string => {
    return `replica_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };

  // Write a replica to Firestore
  const saveToFirestore = useCallback(async (replica: UserReplica) => {
    try {
      const sanitized = sanitizeForFirestore(replica);
      await setDoc(doc(db, COLLECTION_NAME, replica.id), {
        ...sanitized,
        _updatedAt: serverTimestamp(),
      });
      logger.debug('Replica saved to Firestore', { id: replica.id });
    } catch (error) {
      logger.error('Failed to save replica to Firestore:', error);
    }
  }, []);

  // Update a replica in Firestore
  const updateInFirestore = useCallback(async (id: string, updates: Record<string, any>) => {
    try {
      const sanitized = sanitizeForFirestore(updates);
      await updateDoc(doc(db, COLLECTION_NAME, id), {
        ...sanitized,
        _updatedAt: serverTimestamp(),
      });
      logger.debug('Replica updated in Firestore', { id });
    } catch (error) {
      logger.error('Failed to update replica in Firestore:', error);
    }
  }, []);

  // Delete a replica from Firestore
  const deleteFromFirestore = useCallback(async (id: string) => {
    try {
      await deleteDoc(doc(db, COLLECTION_NAME, id));
      logger.debug('Replica deleted from Firestore', { id });
    } catch (error) {
      logger.error('Failed to delete replica from Firestore:', error);
    }
  }, []);

  const convertFormDataToReplica = (formData: ReplicaFormData, creatorId: string, parentId?: string | null): UserReplica => {
    const now = new Date().toISOString();

    const skills = [
      formData.expertise ? 'Domain Expertise' : '',
      formData.language !== 'English' ? 'Multilingual Communication' : '',
      formData.selectedPersonalityTraits.length > 0 ? 'Personality Adaptation' : '',
      formData.voiceSamples.length > 0 ? 'Voice Synthesis' : '',
      formData.trainingMaterials.length > 0 ? 'Custom Knowledge Base' : '',
    ].filter(Boolean);

    const supportedTasks = [
      `${formData.category} Support`,
      'Customer Interaction',
      'Information Retrieval',
      'Task Automation',
    ];

    return {
      id: generateReplicaId(),
      name: formData.name,
      category: formData.category as any,
      price: {
        amount: 25.99,
        period: 'hour',
      },
      hasHumanGuardian: formData.guardianAssignment !== 'no-guardian',
      image: 'https://images.pexels.com/photos/8386440/pexels-photo-8386440.jpeg',
      rating: 4.5,
      reviews: 0,
      description: formData.description,
      skills,
      supportedTasks,
      creatorId,
      status: 'draft',
      createdAt: now,
      updatedAt: now,
      formData,
      parentId: parentId || null,
    };
  };

  const convertAgentToReplica = (agent: AIAgent, creatorId: string): UserReplica => {
    const now = new Date().toISOString();

    const defaultFormData: ReplicaFormData = {
      name: agent.name,
      category: agent.category,
      description: agent.description,
      avatarStyle: 'realistic',
      gender: 'neutral',
      ageRange: 'adult',
      ethnicity: 'diverse',
      voiceTone: 'professional',
      voiceSpeed: 1.0,
      voiceSamples: [],
      expertise: agent.skills.join(', '),
      language: 'English',
      responseSpeed: 'fast',
      selectedPersonalityTraits: ['Professional & Formal'],
      personalityDescription: `Professional ${agent.category} specialist with expertise in ${agent.skills.slice(0, 3).join(', ')}.`,
      trainingMaterials: [],
      sampleConversations: '',
      dosAndDonts: '',
      deploymentOption: 'website-widget',
      guardianAssignment: agent.hasHumanGuardian ? 'auto-assign' : 'no-guardian',
    };

    return {
      id: generateReplicaId(),
      name: agent.name,
      category: agent.category,
      price: agent.price,
      hasHumanGuardian: agent.hasHumanGuardian,
      image: agent.image,
      rating: agent.rating,
      reviews: agent.reviews,
      description: agent.description,
      skills: agent.skills,
      supportedTasks: agent.supportedTasks,
      guardian: agent.guardian,
      creatorId,
      status: 'deployed',
      createdAt: now,
      updatedAt: now,
      formData: defaultFormData,
      parentId: null,
    };
  };

  const addReplica = (formData: ReplicaFormData, creatorId: string, parentId?: string | null): UserReplica => {
    const newReplica = convertFormDataToReplica(formData, creatorId, parentId);
    // Optimistic update — Firestore onSnapshot will confirm
    setUserReplicas(prev => [...prev, newReplica]);
    saveToFirestore(newReplica);
    return newReplica;
  };

  const hireMarketplaceAgent = (agent: AIAgent, creatorId: string): UserReplica => {
    const newReplica = convertAgentToReplica(agent, creatorId);
    setUserReplicas(prev => [...prev, newReplica]);
    saveToFirestore(newReplica);
    return newReplica;
  };

  const updateReplica = (id: string, updates: Partial<UserReplica>) => {
    const updatedAt = new Date().toISOString();
    setUserReplicas(prev =>
      prev.map(replica =>
        replica.id === id
          ? { ...replica, ...updates, updatedAt }
          : replica
      )
    );
    updateInFirestore(id, { ...updates, updatedAt });
  };

  const deployReplica = (id: string) => {
    const updatedAt = new Date().toISOString();
    setUserReplicas(prev =>
      prev.map(replica =>
        replica.id === id
          ? { ...replica, status: 'deployed', updatedAt }
          : replica
      )
    );
    updateInFirestore(id, { status: 'deployed', updatedAt });
  };

  const deleteReplica = (id: string) => {
    setUserReplicas(prev => prev.filter(replica => replica.id !== id));
    deleteFromFirestore(id);
  };

  const getReplicasByStatus = (status: ReplicaStatus, creatorId?: string): UserReplica[] => {
    return userReplicas.filter(replica => {
      const matchesStatus = replica.status === status;
      const matchesCreator = !creatorId || replica.creatorId === creatorId;
      return matchesStatus && matchesCreator;
    });
  };

  const getUserReplicas = (creatorId: string): UserReplica[] => {
    return userReplicas.filter(replica => replica.creatorId === creatorId);
  };

  const getReplicaById = (id: string): UserReplica | undefined => {
    return userReplicas.find(replica => replica.id === id);
  };

  const updateReplicaHierarchy = (updates: HierarchyUpdate[]) => {
    setUserReplicas(prev =>
      prev.map(replica => {
        const update = updates.find(u => u.id === replica.id);
        if (update) {
          const updatedAt = new Date().toISOString();
          updateInFirestore(replica.id, { parentId: update.parentId, updatedAt });
          return {
            ...replica,
            parentId: update.parentId,
            updatedAt,
          };
        }
        return replica;
      })
    );
  };

  const buildHierarchyTree = (replicas: UserReplica[], parentId: string | null = null, level: number = 0): TeamHierarchyNode[] => {
    const children = replicas.filter(replica => replica.parentId === parentId);

    return children.map(replica => ({
      replica,
      children: buildHierarchyTree(replicas, replica.id, level + 1),
      level
    }));
  };

  const getTeamHierarchy = (creatorId: string): TeamHierarchyNode[] => {
    const userReplicasForCreator = getUserReplicas(creatorId);
    return buildHierarchyTree(userReplicasForCreator);
  };

  const getMarketplaceAgents = (): AIAgent[] => {
    const deployedReplicas = getReplicasByStatus('deployed');
    const deployedAsAgents: AIAgent[] = deployedReplicas.map(replica => ({
      id: replica.id,
      name: replica.name,
      category: replica.category,
      price: replica.price,
      hasHumanGuardian: replica.hasHumanGuardian,
      image: replica.image,
      rating: replica.rating,
      reviews: replica.reviews,
      description: replica.description,
      skills: replica.skills,
      supportedTasks: replica.supportedTasks,
      guardian: replica.guardian,
    }));

    return [...agents, ...deployedAsAgents];
  };

  const value: ReplicaContextType = {
    userReplicas,
    addReplica,
    updateReplica,
    deployReplica,
    deleteReplica,
    getReplicasByStatus,
    getMarketplaceAgents,
    getUserReplicas,
    getReplicaById,
    updateReplicaHierarchy,
    getTeamHierarchy,
    hireMarketplaceAgent,
  };

  return <ReplicaContext.Provider value={value}>{children}</ReplicaContext.Provider>;
};
